import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import DataTable from 'components/shared/DataTable';
import Foldout from 'components/shared/Foldout';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import Search from 'components/shared/Search';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Client } from 'models/flexible/Client';
import { adminClientsQueryKeys, adminIndexClients } from 'services/api/admin/clients';
import type { AdminClientIndexFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import TrashIcon from 'svg/TrashIcon';

type Props = {
  promptId: string;
  promptName?: string;
  handleClose: () => void;
  isEditing?: boolean;
  isCopying?: boolean;
  isPublished?: boolean;
  handleApply?: (added: Client[], removed: string[]) => void;
  initialAddedClients?: Client[];
  initialRemovedClientIds?: string[];
};

export default function AssociatedClientsSlideout({
  promptId,
  promptName,
  handleClose,
  isEditing = false,
  isCopying = false,
  isPublished = false,
  handleApply,
  initialAddedClients = [],
  initialRemovedClientIds = [],
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [addedClients, setAddedClients] = useState<Client[]>(initialAddedClients);
  const [removedClientIds, setRemovedClientIds] = useState<Set<string>>(new Set(initialRemovedClientIds));

  useEffect(() => {
    setOpen(true);
  }, []);

  const animateClose = useCallback(() => {
    setOpen(false);
    setTimeout(handleClose, 500);
  }, [handleClose]);

  const serverQuery = useInfiniteQuery({
    queryKey: adminClientsQueryKeys.index({ aiPrompt: isCopying ? undefined : promptId }),
    queryFn: ({ pageParam, signal }) =>
      adminIndexClients({ aiPrompt: promptId, sortBy: 'name asc', page: pageParam, pageSize: 25 }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined,
    enabled: !!promptId && !isCopying,
  });

  const dropdownOpts = useInfiniteOptions({
    queryKey: [
      'adminClients',
      'workspacePromptsDropdown',
      { 'aiPrompt.not': !isCopying ? promptId : undefined, clientType: !isPublished ? 'Admin' : undefined },
    ],
    fetchFn: adminIndexClients,
    params: {
      ...(!isCopying ? { 'aiPrompt.not': promptId } : {}),
      ...(!isPublished ? { clientType: 'Admin' } : {}),
      sortBy: 'name asc',
    },
  });

  const serverClients = useMemo(() => serverQuery.data?.pages.flatMap((p) => p.data) ?? [], [serverQuery.data]);
  const serverTotalRecords = serverQuery.data?.pages[0]?.meta?.totalRecords ?? 0;

  const addedClientIds = useMemo(() => new Set(addedClients.map((c) => c.id)), [addedClients]);

  const handleAddClient = (client: Client) => {
    if (removedClientIds.has(client.id)) {
      setRemovedClientIds((prev) => {
        const next = new Set(prev);
        next.delete(client.id);
        return next;
      });
    } else {
      setAddedClients((prev) => [...prev, client]);
    }
  };

  const handleRemoveClient = (client: Client) => {
    if (addedClients.some((c) => c.id === client.id)) {
      setAddedClients((prev) => prev.filter((c) => c.id !== client.id));
    } else {
      setRemovedClientIds((prev) => new Set([...prev, client.id]));
    }
  };

  const handleApplyAndClose = () => {
    handleApply?.(addedClients, Array.from(removedClientIds));
    animateClose();
  };

  const tableData = useMemo(() => {
    const filtered = serverClients.filter((c) => !removedClientIds.has(c.id));
    const filteredAdded = search
      ? addedClients.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
      : addedClients;
    const filteredServer = search
      ? filtered.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
      : filtered;
    return [...filteredAdded, ...filteredServer];
  }, [serverClients, addedClients, removedClientIds, search]);

  const displayCount = Math.max(0, serverTotalRecords + addedClients.length - removedClientIds.size);

  const columnHelper = createColumnHelper<Client<AdminClientIndexFields> | Client>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Client Name',
        cell: (info) => <strong>{info.getValue()}</strong>,
      }),
      columnHelper.accessor('clientType', {
        header: 'Type',
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      ...(isEditing
        ? [
            columnHelper.display({
              id: 'actions',
              header: '',
              size: 48,
              meta: { fixedWidth: true },
              cell: (info) => (
                <TrashIcon
                  color={colors.red100}
                  width={16}
                  height={16}
                  onClick={() => handleRemoveClient(info.row.original)}
                  style={{ cursor: 'pointer' }}
                />
              ),
            }),
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEditing, handleRemoveClient]
  );

  const title = `${promptName ? `${promptName} - ` : ''}Associated Clients`;

  return (
    <Foldout open={open} position='right' onBackdropClick={animateClose} width='min(85%, 917px)'>
      <PanelContainer>
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
          <CloseButton onClick={animateClose}>&times;</CloseButton>
        </PanelHeader>

        <PanelContent>
          {isEditing && (
            <InputGroup title='Add Clients'>
              <Select
                {...dropdownOpts}
                value={null}
                onChange={handleAddClient}
                getOptionValue={(o) => o!.id}
                getOptionLabel={(o) => o!.name}
                filterOption={(option) => !addedClientIds.has((option.data as Client).id)}
                placeholder='Search clients to add...'
                isDisabled={!isPublished && !!tableData.length}
              />
            </InputGroup>
          )}

          <TableBar>
            <ItemsCount>
              {displayCount} Associated Client{displayCount !== 1 ? 's' : ''}
            </ItemsCount>
            <Search value={search} onChange={setSearch} placeholder='Search...' />
          </TableBar>

          <DataTable
            columns={columns}
            data={tableData}
            loading={serverQuery.isLoading || serverQuery.isFetchingNextPage}
            hasMore={serverQuery.hasNextPage}
            onBodyEndReached={() => serverQuery.fetchNextPage()}
            defaultSortBy={[{ id: 'name', desc: false }]}
            hasFooter={false}
          />
        </PanelContent>

        <PanelFooter>
          <ButtonGroup>
            {isEditing ? (
              <>
                <Button variant='ghost' onClick={animateClose}>
                  Cancel
                </Button>
                <Button onClick={handleApplyAndClose}>Apply</Button>
              </>
            ) : (
              <Button onClick={animateClose}>Close</Button>
            )}
          </ButtonGroup>
        </PanelFooter>
      </PanelContainer>
    </Foldout>
  );
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const TableBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  > :last-child {
    max-width: 240px;
  }
`;

const ItemsCount = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.black100};
  white-space: nowrap;
`;
